// 📤 B242 MVP2 (AI_112) — EXPORT GOVERNADO da Table 1 em CSV (puro + testável).
// ─────────────────────────────────────────────────────────────────────────────
// Exporta a tabela DESCRITIVA (agregada), NÃO o dado por paciente: nenhum
// identificador sai, e células com n<5 são MASCARADAS (anti-reidentificação),
// igual à tela. Um cabeçalho de metadados registra finalidade/coorte/filtros
// (gate de finalidade — a trilha completa + export por paciente exigem a camada
// de governança/CEP, MVP4). A geração do CSV é pura; o download fica na UI.
import { LinhaTabela1, CelulaTabela1 } from "../types";
import { N_MINIMO_CELULA } from "./estatisticasDescritivas";

export interface MetaExport {
  geradoEm: string; // já formatado pela UI (a função é pura, sem Date)
  finalidade: string;
  n: number;
  filtros: string;
  conector: string;
  estratificacao: string;
}

// Escapa um campo CSV (aspas duplas; aspas internas duplicadas).
function csv(v: unknown): string {
  return `"${String(v ?? "").replace(/"/g, '""')}"`;
}

// Máscara n<5 — MESMA regra da tela (textoCelula): não vaza contagem pequena.
function celulaMascarada(c: CelulaTabela1): string {
  const pequeno = c.pequeno || (typeof c.n === "number" && c.n > 0 && c.n < N_MINIMO_CELULA);
  return pequeno ? `< ${N_MINIMO_CELULA}` : c.texto;
}

export function tabela1ParaCSV(
  meta: MetaExport,
  colunas: string[],
  tabela: LinhaTabela1[]
): string {
  const linhas: string[] = [];

  // Cabeçalho de metadados (governança: finalidade + escopo da coorte).
  linhas.push(csv("Pesquisa Clínica — Tabela descritiva (pseudonimizada, agregada)"));
  linhas.push([csv("Gerado em"), csv(meta.geradoEm)].join(","));
  linhas.push([csv("Finalidade"), csv(meta.finalidade)].join(","));
  linhas.push([csv("Coorte (n)"), csv(meta.n)].join(","));
  linhas.push([csv("Filtros"), csv(meta.filtros)].join(","));
  linhas.push([csv("Combinação"), csv(meta.conector)].join(","));
  linhas.push([csv("Estratificação"), csv(meta.estratificacao)].join(","));
  linhas.push(csv(`Aviso: células com n<${N_MINIMO_CELULA} mascaradas (privacidade).`));
  linhas.push(""); // linha em branco separadora

  // Cabeçalho da tabela.
  linhas.push(
    [csv("Variável"), csv("Medida/Categoria"), csv("Faltantes"), csv("Faltantes %"), ...colunas.map(csv)].join(",")
  );

  // Corpo: uma linha por (variável × rótulo). Faltantes só na 1ª linha da variável.
  for (const linha of tabela) {
    const porColuna: Record<string, CelulaTabela1[]> = {};
    for (const cc of linha.celulasPorColuna) porColuna[cc.coluna] = cc.celulas;
    const rotulos = (porColuna["Total"] || []).map((c) => c.rotulo);

    rotulos.forEach((rotulo, idx) => {
      const celulasColuna = colunas.map((col) => {
        const cel = (porColuna[col] || []).find((c) => c.rotulo === rotulo);
        return csv(cel ? celulaMascarada(cel) : "—");
      });
      linhas.push(
        [
          csv(idx === 0 ? linha.label : ""),
          csv(rotulo),
          csv(idx === 0 ? linha.faltantes : ""),
          csv(idx === 0 ? linha.faltantesPct : ""),
          ...celulasColuna,
        ].join(",")
      );
    });
  }

  return linhas.join("\r\n");
}
